'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import * as React from 'react'

// lib
import { distribute } from '../_support'
import Button, { ButtonProps } from '../button'
import { DropDownIcon, IconProps } from '../icons'
import SVG from '../svg'
import { ThemeVariantContext, ThemeVariants } from '../_support'

/* -----------------------------------------------------------------------------
 * SelectButton
 * -------------------------------------------------------------------------- */

type SupportedVariants = Extract<
  ThemeVariants,
  'default' | 'violet' | 'gray' | 'punchThroughGray' | 'punchThroughPurple'
>

type Variants = Record<SupportedVariants, ButtonProps & IconProps>

const variants: Variants = {
  default: {
    fill: 'violet',
    variant: 'paleViolet'
  },
  violet: {
    variant: 'violet',
    fill: 'white'
  },
  gray: {
    fill: 'shade06',
    variant: 'gray'
  },
  punchThroughGray: {
    fill: 'purple',
    variant: 'punchThroughGray'
  },
  punchThroughPurple: {
    fill: 'purple',
    variant: 'punchThroughPurple'
  }
}

export type SelectButtonProps = Omit<ButtonProps, 'children'> & {
  children: React.ReactNode
  disabled?: boolean
  showIcon?: boolean
}

const SelectButton = React.forwardRef<HTMLElement, SelectButtonProps>(
  (
    { children, disabled = false, showIcon = true, ...rest }: SelectButtonProps,
    ref
  ) => {
    const themeVariant = React.useContext(ThemeVariantContext)
    const props: Omit<SelectButtonProps, 'children' | 'disabled'> &
      Variants[keyof Variants] & { cursor?: string } = {
      ...(disabled ? { cursor: 'default' } : {}),
      ...variants[themeVariant],
      ...rest
    }
    const [iconProps, buttonProps] = distribute(props, {
      using: SVG.styleKeys,
      to: {},
      rest: {}
    })

    return (
      <Button
        shape='rectangular'
        size='small'
        justifyContent='space-between'
        ref={ref}
        disabled={disabled}
        {...buttonProps}
      >
        {children}
        {showIcon && <DropDownIcon ml='12' {...iconProps} />}
      </Button>
    )
  }
)

export default SelectButton
